import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { 
  Users, 
  DownloadSimple, 
  ArrowRight, 
  CurrencyEur, 
  MagnifyingGlass,
  Phone,
  EnvelopeSimple,
  MapPin,
  Buildings,
  CalendarBlank,
  TrendUp,
  FunnelSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API } from '@/App';

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('revenue'); // revenue, name, recent

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/clients`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Filter and sort clients
  const filteredClients = clients
    .filter(client => 
      client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'revenue') return (b.total_revenue || 0) - (a.total_revenue || 0);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return 0;
    });

  // Calculate totals
  const totalRevenue = clients.reduce((sum, c) => sum + (c.total_revenue || 0), 0);
  const totalQuotes = clients.reduce((sum, c) => sum + (c.total_quotes || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get color based on name hash
  const getAvatarColor = (name) => {
    const colors = [
      'from-violet-500 to-purple-600',
      'from-blue-500 to-cyan-600',
      'from-emerald-500 to-teal-600',
      'from-amber-500 to-orange-600',
      'from-rose-500 to-pink-600',
      'from-indigo-500 to-blue-600'
    ];
    const index = (name || '').charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6"
    >
      {/* Header - Desktop */}
      <div className="hidden lg:flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clients</h1>
          <p className="text-muted-foreground">{clients.length} clients enregistrés</p>
        </div>
        
        <div className="flex items-center gap-3">
          {clients.length > 0 && (
            <button 
              onClick={handleExportCSV}
              className="px-4 py-2 rounded-xl bg-muted hover:bg-muted/80 flex items-center gap-2 text-sm font-medium text-foreground transition-colors"
              data-testid="export-clients-btn"
            >
              <DownloadSimple size={18} />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Header - Mobile */}
      <div className="flex lg:hidden items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Users size={20} className="text-violet-600" weight="duotone" />
          </div>
          <div>
            <span className="text-lg font-semibold text-foreground">Clients</span>
            <span className="text-sm text-muted-foreground ml-2">({clients.length})</span>
          </div>
        </div>
        
        {clients.length > 0 && (
          <button 
            onClick={handleExportCSV}
            className="w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
          >
            <DownloadSimple size={20} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Stats Cards - Desktop */}
      {clients.length > 0 && (
        <div className="hidden lg:grid grid-cols-3 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="premium-card p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Users size={24} className="text-violet-600" weight="duotone" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total clients</p>
                <p className="text-3xl font-bold text-foreground">{clients.length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="premium-card p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <CurrencyEur size={24} className="text-amber-600" weight="duotone" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CA total</p>
                <p className="text-3xl font-bold text-foreground">{totalRevenue.toLocaleString('fr-FR')} €</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="premium-card p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <TrendUp size={24} className="text-emerald-600" weight="duotone" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Panier moyen</p>
                <p className="text-3xl font-bold text-foreground">
                  {clients.length > 0 ? Math.round(totalRevenue / clients.length).toLocaleString('fr-FR') : 0} €
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Search & Filter */}
      {clients.length > 0 && (
        <div className="flex gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass 
              size={18} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <Input
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-11 h-11 rounded-xl bg-muted/50 border-0"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-11 px-4 rounded-xl bg-muted/50 border-0 text-sm font-medium text-foreground"
          >
            <option value="revenue">CA décroissant</option>
            <option value="name">Nom A-Z</option>
          </select>
        </div>
      )}

      {/* Clients List - Desktop Table */}
      <div className="hidden lg:block">
        {filteredClients.length > 0 ? (
          <div className="premium-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">Client</th>
                  <th className="text-left p-4 font-medium text-muted-foreground text-sm">Contact</th>
                  <th className="text-center p-4 font-medium text-muted-foreground text-sm">Devis</th>
                  <th className="text-right p-4 font-medium text-muted-foreground text-sm">CA Total</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client, index) => (
                  <motion.tr
                    key={client.client_id || client.email}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => navigate(`/clients/${encodeURIComponent(client.email)}`)}
                    className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                    data-testid={`client-row-${client.client_id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getAvatarColor(client.name)} flex items-center justify-center text-white font-semibold text-sm`}>
                          {getInitials(client.name)}
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{client.name}</p>
                          {client.company && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Buildings size={12} />
                              {client.company}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        <p className="text-sm text-foreground flex items-center gap-2">
                          <EnvelopeSimple size={14} className="text-muted-foreground" />
                          {client.email}
                        </p>
                        {client.phone && (
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Phone size={14} />
                            {client.phone}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-muted text-sm font-semibold text-foreground">
                        {client.total_quotes || 0}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <p className="text-lg font-bold text-amber-600">{(client.total_revenue || 0).toLocaleString('fr-FR')} €</p>
                    </td>
                    <td className="p-4">
                      <ArrowRight size={18} className="text-muted-foreground" />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : searchTerm ? (
          <div className="premium-card p-12 text-center">
            <MagnifyingGlass size={48} className="text-muted-foreground mx-auto mb-4" weight="duotone" />
            <p className="text-foreground font-medium">Aucun résultat</p>
            <p className="text-sm text-muted-foreground mt-1">Aucun client ne correspond à "{searchTerm}"</p>
          </div>
        ) : (
          <div className="premium-card p-12 text-center">
            <div className="w-20 h-20 rounded-3xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-4">
              <Users size={40} className="text-violet-600" weight="duotone" />
            </div>
            <p className="text-xl font-semibold text-foreground">Aucun client</p>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Vos clients apparaîtront ici automatiquement après la création de devis. 
              Commencez par créer votre premier devis !
            </p>
          </div>
        )}
      </div>

      {/* Clients List - Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {filteredClients.length > 0 ? (
          filteredClients.map((client, index) => (
            <motion.div
              key={client.client_id || client.email}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/clients/${encodeURIComponent(client.email)}`)}
              className="premium-card premium-card-hover p-4"
              data-testid={`client-card-${client.client_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getAvatarColor(client.name)} flex items-center justify-center text-white font-semibold`}>
                  {getInitials(client.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">{client.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-amber-600">{(client.total_revenue || 0).toFixed(0)} €</p>
                  <p className="text-xs text-muted-foreground">{client.total_quotes || 0} devis</p>
                </div>
                <ArrowRight size={18} className="text-muted-foreground flex-shrink-0" />
              </div>
            </motion.div>
          ))
        ) : searchTerm ? (
          <div className="premium-card p-8 text-center">
            <MagnifyingGlass size={32} className="text-muted-foreground mx-auto mb-3" weight="duotone" />
            <p className="text-foreground font-medium">Aucun résultat</p>
          </div>
        ) : (
          <div className="premium-card p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Users size={32} className="text-muted-foreground" weight="duotone" />
            </div>
            <p className="text-foreground font-medium">Aucun client</p>
            <p className="text-sm text-muted-foreground mt-1">Créez votre premier devis</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Clients;
